/**
 * ChordTrack Component
 * Dedicated DAW timeline track displaying harmonic progression, chord symbols,
 * real-time playback highlight, and click-to-audition chords for Chord Notator.
 */

import React, { useState } from 'react';
import { DetectedChord, TimeSignature } from '../types/midi';
import { AudioEngine } from '../core/audioEngine';
import { chordSymbolToPitches } from '../core/chordDetector';
import { getTicksPerMeasure } from '../core/timing';
import { Volume2, Edit2, Check } from 'lucide-react';

interface ChordTrackProps {
  measureChords: Map<number, DetectedChord[]>;
  totalMeasures: number;
  timeSignature: TimeSignature;
  ppq: number;
  pixelsPerTick: number;
  currentTick: number;
  audioEngine?: AudioEngine;
  onUpdateChord?: (measureIndex: number, newChordSymbol: string) => void;
  height?: number; // default 32px
}

export const ChordTrack: React.FC<ChordTrackProps> = ({
  measureChords,
  totalMeasures,
  timeSignature,
  ppq,
  pixelsPerTick,
  currentTick,
  audioEngine,
  onUpdateChord,
  height = 32,
}) => {
  const measureTicks = getTicksPerMeasure(timeSignature, ppq);

  const [editingMeasure, setEditingMeasure] = useState<number | null>(null);
  const [editText, setEditText] = useState<string>('');

  const handleAuditionChord = (chord: DetectedChord, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!audioEngine) return;
    const pitches = chordSymbolToPitches(chord);
    pitches.forEach((pitch, idx) => {
      // Arpeggiate slightly for a rich keyboard preview
      setTimeout(() => {
        audioEngine.playNote(pitch, 90, 0.6);
      }, idx * 25);
    });
  };

  const handleStartEdit = (measureIdx: number, currentSymbol: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingMeasure(measureIdx);
    setEditText(currentSymbol || '');
  };

  const handleSaveEdit = (measureIdx: number) => {
    if (onUpdateChord && editText.trim()) {
      onUpdateChord(measureIdx, editText.trim());
    }
    setEditingMeasure(null);
  };

  return (
    <div
      id="piano-roll-chord-track"
      style={{ height: `${height}px` }}
      className="relative bg-[#181614] border-b border-[#2D2820] flex items-center select-none overflow-hidden"
    >
      {Array.from({ length: totalMeasures }).map((_, mIdx) => {
        const mStartTick = mIdx * measureTicks;
        const x = mStartTick * pixelsPerTick;
        const width = measureTicks * pixelsPerTick;
        const chords = measureChords.get(mIdx) || [];
        const isEditing = editingMeasure === mIdx;

        return (
          <div
            key={`chord-measure-container-${mIdx}`}
            id={`chord-track-measure-${mIdx + 1}`}
            style={{
              left: `${x}px`,
              width: `${width}px`,
              height: `${height - 4}px`,
            }}
            className="absolute top-0.5 border-r border-[#2D2820] flex items-center"
          >
            {isEditing ? (
              <div className="flex items-center gap-1 w-full px-2" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit(mIdx);
                    if (e.key === 'Escape') setEditingMeasure(null);
                  }}
                  autoFocus
                  className="w-full bg-[#1C1A17] text-[#F5C767] font-mono text-xs px-1.5 py-0.5 border border-[#F5C767] focus:outline-none"
                  placeholder="e.g. F C or F - C"
                />
                <button
                  type="button"
                  onClick={() => handleSaveEdit(mIdx)}
                  className="p-0.5 text-[#F5C767] hover:text-amber-300"
                >
                  <Check className="w-3 h-3" />
                </button>
              </div>
            ) : chords.length === 0 ? (
              <div
                className="w-full h-full px-2 flex items-center justify-between text-[#A89F91]/40 hover:bg-[#221F1B]/40 transition-colors cursor-pointer"
                onDoubleClick={(e) => handleStartEdit(mIdx, '', e)}
                title={`Measure ${mIdx + 1}: No chord detected (Double-click to set)`}
              >
                <span className="text-[9px] font-mono text-[#A89F91]/60">M{mIdx + 1}</span>
                <span className="text-[10px] font-mono italic">—</span>
              </div>
            ) : (
              chords.map((chord, cIdx) => {
                const cStart = chord.startTick ?? mStartTick;
                const cDur = chord.durationTicks ?? Math.floor(measureTicks / chords.length);
                const chordLeft = Math.max(0, (cStart - mStartTick) * pixelsPerTick);
                const chordWidth = Math.max(28, cDur * pixelsPerTick);
                const isChordActive = currentTick >= cStart && currentTick < cStart + cDur;

                return (
                  <div
                    key={`chord-block-${mIdx}-${cIdx}`}
                    style={{
                      left: `${chordLeft}px`,
                      width: `${chordWidth}px`,
                      height: `${height - 4}px`,
                    }}
                    className={`absolute top-0 px-1.5 flex items-center justify-between border-r border-[#3D362C]/40 transition-all group ${
                      isChordActive
                        ? 'bg-[#F5C767]/20 border-t-2 border-t-[#F5C767] text-[#F5C767] shadow-[0_0_10px_rgba(245,199,103,0.3)] z-10'
                        : 'bg-[#221F1B] hover:bg-[#2C2722] text-[#EBE6DB] border-y border-[#3D362C]/60'
                    }`}
                    title={`Measure ${mIdx + 1} (${chord.symbol}): Click icon to audition, double-click to edit`}
                    onDoubleClick={(e) =>
                      handleStartEdit(mIdx, chords.map((c) => c.symbol).join(' '), e)
                    }
                  >
                    <div className="flex items-center gap-1 min-w-0">
                      {cIdx === 0 && (
                        <span className="text-[9px] font-mono text-[#A89F91] mr-0.5">
                          M{mIdx + 1}
                        </span>
                      )}
                      <span
                        className={`font-mono font-bold text-xs tracking-tight truncate ${
                          isChordActive ? 'text-[#F5C767]' : 'text-[#F0EBE0]'
                        }`}
                      >
                        {chord.symbol}
                      </span>
                    </div>

                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                      <button
                        type="button"
                        onClick={(e) => handleAuditionChord(chord, e)}
                        className="p-0.5 rounded text-[#A89F91] hover:text-[#F5C767] hover:bg-[#F5C767]/15 transition-colors"
                        title="Audition chord"
                      >
                        <Volume2 className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) =>
                          handleStartEdit(mIdx, chords.map((c) => c.symbol).join(' '), e)
                        }
                        className="p-0.5 rounded text-[#A89F91] hover:text-[#F5C767] hover:bg-[#F5C767]/15 transition-colors"
                        title="Edit chord"
                      >
                        <Edit2 className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        );
      })}
    </div>
  );
};
